/** Loads and exposes all static game configuration from JSON data files. */
import type { MovementConfig, CombatTuningConfig, EnemyArchetypeConfig, TierThresholds, HideTuningConfig, StealthTuningConfig, AttackTuningConfig, AttackVisualConfig, V4TreeDefinition, V4NodeDefinition, V4ApotheosisDefinition, V4NodeType, V4ForkDefinition, V4SkillProgressionConfig, DamageConfig } from '../utils/Types';
import movementData from '../data/tuning/movement.json';
import combatData from '../data/tuning/combat.json';
import punchTuningData from '../data/tuning/skills/punch.json';
import punchmoreTuningData from '../data/tuning/skills/punchmore.json';
import dashAttackTuningData from '../data/tuning/skills/dash-attack.json';
import stabTuningData from '../data/tuning/skills/stab.json';
import quickbladeTuningData from '../data/tuning/skills/quickblade.json';
import skillProgressionData from '../data/skill-progression.json';
import hideTuningData from '../data/tuning/skills/hide.json';
import stealthTuningData from '../data/tuning/skills/stealth.json';
import { archetypes } from '../data/enemies/index';
import { v4SkillTrees } from '../data/trees/index';
import physicsData from '../data/tuning/physics.json';
import sector1Data from '../data/sectors/sector-1.json';


/** One rectangular block of a building's footprint (tile coordinates). */
export interface FootprintBlock {
  colMin: number; colMax: number;
  rowMin: number; rowMax: number;
}

/** Data-driven building definition — everything else is derived at runtime. */
export interface BuildingConfig {
  imageKey: string;
  centerX: number;
  centerY: number;
  widthInTiles: number;
  footprint: FootprintBlock[];
}

/** A single enemy definition within a spawn point group. */
export interface SpawnPointEnemy {
  archetypeId: string;
  dutyX: number;
  dutyY: number;
  /** Per-enemy initial spawn override (snapped to nearest walkable tile). */
  spawnX?: number;
  spawnY?: number;
  /** Per-enemy respawn override (snapped to nearest walkable tile). */
  respawnX?: number;
  respawnY?: number;
}

/** A named group of enemies that appear at a garrison location. */
export interface SpawnPointConfig {
  id: string;
  /** Initial spawn position for enemies in this group (world coords). */
  x: number;
  y: number;
  /** Group-level respawn override — used when enemy has no per-enemy respawn. */
  respawnX?: number;
  respawnY?: number;
  enemies: SpawnPointEnemy[];
}

/** Runtime sector config loaded from JSON. */
export interface SectorConfig {
  id: string;
  name: string;
  hubSpawn: { x: number; y: number };
  spawnPoints: SpawnPointConfig[];
  respawnTimerMs: number;
  maxActive: number;
  buildings?: BuildingConfig[];
}


/** Static accessor for all JSON-backed game configs; no instantiation needed. */
export class ConfigLoader {
  /** Movement tuning values (walk speed, run speed, dash params) loaded from tuning/movement.json. */
  static readonly movement: MovementConfig = movementData as MovementConfig;
  /** Combat tuning values (damage, knockback, hitstop, attack timings) loaded from tuning/combat.json. */
  static readonly combat: CombatTuningConfig = combatData as CombatTuningConfig;
  /** Physics tuning values (body separation force and path-diversity perturbation) loaded from tuning/physics.json. */
  static readonly physics: {
    separation: { radius: number; force: number };
    pathDiversity: { perturbation: number; minDistanceTiles: number };
  } = physicsData;

  /** Registry of per-attack-type tuning configs loaded from individual JSON files. */
  private static readonly attackConfigs = new Map<string, AttackTuningConfig>([
    ['punch', punchTuningData as AttackTuningConfig],
    ['punchmore', punchmoreTuningData as AttackTuningConfig],
    ['dash_attack', dashAttackTuningData as AttackTuningConfig],
    ['stab', stabTuningData as AttackTuningConfig],
    ['quickblade', quickbladeTuningData as AttackTuningConfig],
  ]);
  /** All enemy archetype definitions loaded from the enemies data index. */
  static readonly enemies: EnemyArchetypeConfig[] = archetypes;

  /** Hide skill tuning values (detection radius, timing windows) loaded from tuning/skills/hide.json. */
  static readonly hideTuning: HideTuningConfig = hideTuningData as unknown as HideTuningConfig;
  /** Stealth skill tuning values (awareness decay, detection thresholds) loaded from tuning/skills/stealth.json. */
  static readonly stealthTuning: StealthTuningConfig = stealthTuningData as StealthTuningConfig;

  // --- V4 Tree System ---

  /** V4 tree definitions indexed by tree name. */
  static readonly v4Trees: Record<string, V4TreeDefinition> = v4SkillTrees;

  /** XP gain config including fizzle and tier multiplier settings (ADR-038). */
  static readonly xpGainConfig = (skillProgressionData as unknown as { xpGain: V4SkillProgressionConfig['xpGain'] }).xpGain;

  /** Effectiveness multiplier config — rewards contextually competent skill use. */
  static readonly effectivenessConfig: { defaultCap: number; enabled: boolean } = (() => {
    const raw = skillProgressionData as unknown as { xpGain: { effectiveness?: { defaultCap?: number; enabled?: boolean } } };
    const eff = raw.xpGain?.effectiveness;
    return { defaultCap: eff?.defaultCap ?? 2.0, enabled: eff?.enabled ?? false };
  })();

  /** Tree XP tier thresholds — base values used by getCurrentTier for UI display. */
  static readonly treeXpThresholds = (skillProgressionData as unknown as { treeXpThresholds: Record<string, TierThresholds> }).treeXpThresholds;

  /** V4 hotbar config — sourced from skill-progression.json attunement block. */
  static readonly v4Hotbar = (() => {
    const attunement = (skillProgressionData as unknown as { attunement: { slotCount: number; foundationSkills: string[] } }).attunement;
    return {
      slotCount: attunement.slotCount,
      foundationSkills: attunement.foundationSkills as string[],
    };
  })();

  /** Default selected trees for a fresh run — sourced from skill-progression.json. */
  static getDefaultSelectedTrees(): string[] {
    return (skillProgressionData as unknown as { attunement: { defaultSelectedTrees: string[] } }).attunement.defaultSelectedTrees;
  }

  /** Spawner fallback and jitter config — sourced from combat.json. */
  static getSpawnerConfig(): { defaultRespawnTimerMs: number; defaultMaxActive: number; pointSpawnJitterPx: number; perimeterOffsetPx: number } {
    return (combatData as unknown as { spawner: { defaultRespawnTimerMs: number; defaultMaxActive: number; pointSpawnJitterPx: number; perimeterOffsetPx: number } }).spawner;
  }

  /** Sector configs indexed by zone ID. */
  private static readonly sectorConfigs: Record<string, SectorConfig> = {
    'zone-1': sector1Data as SectorConfig,
  };

  /** Load sector config by zone ID. */
  static getSectorConfig(zoneId: string): SectorConfig | undefined {
    return this.sectorConfigs[zoneId];
  }

  // --- Per-attack-type tuning lookups ---

  /** Return full attack config for the given type, or undefined if not registered. */
  static getAttackConfig(attackType: string): AttackTuningConfig | undefined {
    return this.attackConfigs.get(attackType);
  }

  /** Return attack timing for the given type. Registered attacks read from their JSON; others from combat.json. */
  static getAttackTiming(attackType: string): { windupMs: number; activeMs: number; recoveryMs: number } {
    return this.attackConfigs.get(attackType)?.timing ?? this.combat.attackTimings[attackType];
  }

  /** Return hitstop frames for the given attack type, or undefined if none configured. */
  static getHitstopFrames(attackType: string): number | undefined {
    return this.attackConfigs.get(attackType)?.hitstop.frames ?? this.combat.hitstop[attackType]?.hitstopFrames;
  }

  /** Return knockback multiplier for the given attack type (defaults to 1.0). */
  static getKnockbackMultiplier(attackType: string): number {
    return this.attackConfigs.get(attackType)?.knockback.multiplier ?? this.combat.knockback.attackMultipliers[attackType] ?? 1.0;
  }

  /** Return attack visual config for the given type, or undefined if none configured. */
  static getAttackVisuals(attackType: string): AttackVisualConfig | undefined {
    return this.attackConfigs.get(attackType)?.visuals ?? this.combat.attackVisuals[attackType];
  }

  /** Return damage config for the given attack type. Registered attacks read from their JSON; others from combat.json defaults. */
  static getDamageConfig(attackType: string): DamageConfig {
    return this.attackConfigs.get(attackType)?.damage ?? this.combat.damage;
  }

  /** Return stagger duration for the given attack type, or 0 if none. */
  static getStaggerDurationMs(attackType: string): number {
    return this.attackConfigs.get(attackType)?.stagger?.durationMs ?? 0;
  }

  /** Look up an enemy archetype config by ID. */
  static getEnemy(id: string): EnemyArchetypeConfig | undefined {
    return this.enemies.find(e => e.id === id);
  }

  /** Return the placeholder sprite color for an archetype (parsed from its JSON config). Defaults to 0xcc3333. */
  static getArchetypeColor(archetypeId: string): number {
    const config = this.getEnemy(archetypeId);
    if (config?.placeholderColor) {
      return parseInt(config.placeholderColor.replace('0x', ''), 16);
    }
    return 0xcc3333;
  }

  // --- V4 Node Lookups (cached for O(1) access) ---

  /** Node-by-ID cache built once at module load. */
  private static v4NodeCache: Map<string, V4NodeDefinition | V4ApotheosisDefinition>;
  /** Node-ID → tree-name cache built once at module load. */
  private static v4NodeTreeCache: Map<string, string>;

  /** Build both V4 caches by iterating all trees once. */
  private static buildV4Caches(): void {
    const nodeMap = new Map<string, V4NodeDefinition | V4ApotheosisDefinition>();
    const treeMap = new Map<string, string>();
    for (const [treeName, tree] of Object.entries(this.v4Trees)) {
      nodeMap.set(tree.foundation.id, tree.foundation);
      treeMap.set(tree.foundation.id, treeName);
      for (const node of tree.nodes) {
        nodeMap.set(node.id, node);
        treeMap.set(node.id, treeName);
      }
      for (const apo of tree.apotheosis) {
        nodeMap.set(apo.id, apo);
        treeMap.set(apo.id, treeName);
      }
    }
    this.v4NodeCache = nodeMap;
    this.v4NodeTreeCache = treeMap;
  }

  /* eslint-disable @typescript-eslint/no-unused-expressions */
  static { this.buildV4Caches(); }
  /* eslint-enable @typescript-eslint/no-unused-expressions */

  /** Look up a V4 node by ID across all trees (foundation, nodes, apotheosis). */
  static getV4Node(id: string): (V4NodeDefinition | V4ApotheosisDefinition) | undefined {
    return this.v4NodeCache.get(id);
  }

  /** Return the tree name that contains the given V4 node ID. */
  static getV4SkillTree(skillId: string): string | undefined {
    return this.v4NodeTreeCache.get(skillId);
  }

  /** Get all nodes from a V4 tree (foundation + nodes + apotheosis) as a flat array. */
  static getV4AllNodes(treeName: string): (V4NodeDefinition | V4ApotheosisDefinition)[] {
    const tree = this.v4Trees[treeName];
    if (!tree) return [];
    return [tree.foundation, ...tree.nodes, ...tree.apotheosis];
  }

  /** Get V4 nodes of a specific type from a tree. */
  static getV4NodesByType(treeName: string, nodeType: V4NodeType): (V4NodeDefinition | V4ApotheosisDefinition)[] {
    return this.getV4AllNodes(treeName).filter(n => n.nodeType === nodeType);
  }

  /** Get the fork definitions for a V4 tree. */
  static getV4Forks(treeName: string): V4ForkDefinition[] {
    return this.v4Trees[treeName]?.forks ?? [];
  }

  /** Find which fork (if any) a node is an option in. */
  static getV4ForkForNode(nodeId: string): { treeName: string; fork: V4ForkDefinition } | undefined {
    for (const [treeName, tree] of Object.entries(this.v4Trees)) {
      for (const fork of tree.forks) {
        if (fork.options.includes(nodeId)) {
          return { treeName, fork };
        }
      }
    }
    return undefined;
  }

  /**
   * Compute the path of nodes from foundation to a target node.
   * Walks parentId chain upward, returns in root-to-target order.
   */
  static getV4PathTo(treeName: string, targetId: string): (V4NodeDefinition | V4ApotheosisDefinition)[] {
    const tree = this.v4Trees[treeName];
    if (!tree) return [];

    const allNodes = this.getV4AllNodes(treeName);
    const byId = new Map<string, V4NodeDefinition | V4ApotheosisDefinition>();
    for (const node of allNodes) byId.set(node.id, node);

    const path: (V4NodeDefinition | V4ApotheosisDefinition)[] = [];
    let current = byId.get(targetId);
    while (current) {
      path.unshift(current);
      const parentId = 'parentId' in current ? current.parentId : undefined;
      if (!parentId || parentId === 'root') break;
      current = byId.get(parentId);
    }
    return path;
  }

  /**
   * Check if a node is reachable given the current fork choices.
   * A node is reachable if every fork on the path from foundation to it
   * was either not yet reached (no choice needed) or the fork choice
   * leads toward this node.
   */
  static isV4NodeReachable(treeName: string, nodeId: string, forkChoices: Record<string, string>): boolean {
    const tree = this.v4Trees[treeName];
    if (!tree) return false;
    if (tree.foundation.id === nodeId) return true;

    // Build path from foundation to this node
    const path = this.getV4PathTo(treeName, nodeId);
    if (path.length === 0) return false;

    // For apotheosis: check if any parent path is reachable
    const apoNode = tree.apotheosis.find(a => a.id === nodeId);
    if (apoNode) {
      return apoNode.parentIds.some(pid => this.isV4NodeReachable(treeName, pid, forkChoices));
    }

    // Check each fork on the path
    for (const fork of tree.forks) {
      const forkChoice = forkChoices[fork.forkId];
      if (!forkChoice) continue; // Fork not yet reached — still reachable

      // Is any fork option on our path?
      const optionOnPath = fork.options.find(optId => path.some(n => n.id === optId));
      if (optionOnPath && optionOnPath !== forkChoice) {
        return false; // Fork was taken on a different branch
      }
    }

    return true;
  }

  // --- Mastery Range Derivation ---

  /** Tier boundaries for mastery range derivation: [500, 2000, 6000, 9000]. */
  static get masteryTierBoundaries(): number[] {
    const t = this.treeXpThresholds;
    return [t.threshold.base, t.transcendent.base, t.apotheosis.base, t.apotheosis.second];
  }

  /** Mastery XP range for a node — how much XP past its threshold to reach 100% success. */
  static getMasteryXpRange(xpThreshold: number): number {
    const boundaries = this.masteryTierBoundaries;
    const ratio = this.xpGainConfig.fizzle.masteryRatio;

    // Find the smallest boundary strictly above the node's threshold
    for (const boundary of boundaries) {
      if (boundary > xpThreshold) {
        return ratio * (boundary - xpThreshold);
      }
    }

    // Fallback for nodes above the highest boundary (shouldn't exist currently)
    return ratio * 3000;
  }

}
